import bcrypt from 'bcryptjs';
import { database, ref, get, update } from '../pages/Auth/db/firebase';

const SESSION_KEY = (facilityId) => `cashier-session-${facilityId}`;

const normalizeEmail = (value) => (value || '').trim().toLowerCase();
const normalizeId = (value) => (value || '').trim().toUpperCase();

const createCashierSession = (facilityId, cashier) => {
  if (!cashier?.id) {
    throw new Error('Invalid cashier record');
  }
  const session = {
    id: cashier.id,
    name: cashier.name || cashier.id,
    email: cashier.email || '',
    facilityId,
    time: Date.now()
  };
  localStorage.setItem(SESSION_KEY(facilityId), JSON.stringify(session));
  return session;
};

const extractCashierRecord = (data, emailLower) => {
  if (!data) return null;
  for (const [cashierKey, cashierValue] of Object.entries(data)) {
    if (!cashierValue || typeof cashierValue !== 'object') continue;
    const candidateEmail = normalizeEmail(cashierValue.email || cashierValue.emailOriginal);
    if (candidateEmail && candidateEmail === emailLower) {
      if (!cashierValue.id) {
        return { id: cashierKey, ...cashierValue };
      }
      return cashierValue;
    }
  }
  return null;
};

export const getCashierSession = (facilityId) => {
  try {
    const raw = localStorage.getItem(SESSION_KEY(facilityId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const logoutCashier = (facilityId) => {
  localStorage.removeItem(SESSION_KEY(facilityId));
};

export const loginCashier = async (facilityId, emailInput, passwordInput) => {
  const emailLower = normalizeEmail(emailInput);
  const passwordRaw = (passwordInput || '').trim();
  const listRef = ref(database, `cashiers/${facilityId}`);
  const snap = await get(listRef);
  if (!snap.exists()) throw new Error('No cashiers registered');
  const data = snap.val();
  const found = extractCashierRecord(data, emailLower);
  if (!found) throw new Error('Unknown email');
  if (!passwordRaw) throw new Error('Enter your password');

  let validPassword = false;
  const storedHash = found.passwordHash || found.password;
  if (storedHash && typeof storedHash === 'string' && storedHash.startsWith('$2')) {
    validPassword = await bcrypt.compare(passwordRaw, storedHash);
  } else if (storedHash) {
    validPassword = storedHash.trim() === passwordRaw;
  } else {
    validPassword = normalizeId(passwordRaw) === normalizeId(found.id);
  }

  if (!validPassword) {
    throw new Error('Invalid password');
  }

  return createCashierSession(facilityId, found);
};

export const updateCashierPassword = async (facilityId, cashierId, newPassword) => {
  const trimmed = (newPassword || '').trim();
  if (!trimmed) {
    throw new Error('Password cannot be empty');
  }
  if (trimmed.length < 6) {
    throw new Error('Password must be at least 6 characters');
  }

  const hash = await bcrypt.hash(trimmed, 10);
  const cashierRef = ref(database, `cashiers/${facilityId}/${cashierId}`);
  const snapshot = await get(cashierRef);
  if (!snapshot.exists()) {
    throw new Error('Cashier record not found');
  }

  await update(cashierRef, {
    passwordHash: hash,
    passwordUpdatedAt: new Date().toISOString()
  });

  const facilityRef = ref(database, `facilities/${facilityId}/cashiers/${cashierId}`);
  const facilitySnap = await get(facilityRef);
  if (facilitySnap.exists()) {
    await update(facilityRef, {
      passwordHash: hash,
      passwordUpdatedAt: new Date().toISOString()
    });
  }

  return hash;
};

export const findCashierByEmailAcrossFacilities = async (emailInput) => {
  const emailLower = normalizeEmail(emailInput);
  if (!emailLower) return null;

  const rootSnap = await get(ref(database, 'cashiers'));
  if (!rootSnap.exists()) return null;

  const facilities = rootSnap.val();
  for (const [facId, roster] of Object.entries(facilities)) {
    const record = extractCashierRecord(roster, emailLower);
    if (record) {
      return { facilityId: facId, cashier: record };
    }
  }

  return null;
};

export const ensureCashierSessionForEmail = async (emailInput) => {
  const found = await findCashierByEmailAcrossFacilities(emailInput);
  if (!found) return null;
  return createCashierSession(found.facilityId, found.cashier);
};

export const persistCashierSession = (facilityId, cashierRecord) => {
  if (!facilityId || !cashierRecord) return null;
  return createCashierSession(facilityId, cashierRecord);
};

export const loginCashierByEmail = async (emailInput, passwordInput) => {
  const found = await findCashierByEmailAcrossFacilities(emailInput);
  if (!found) {
    throw new Error('Unknown email');
  }
  const session = await loginCashier(found.facilityId, emailInput, passwordInput);
  return { facilityId: found.facilityId, session };
};
